import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { BALANCE_FAILURE_LOOKBACK_HOURS, isBalanceFailureReason } from "@/lib/balanceFailure";

export interface BalanceFailureAlertState {
  /** 잔액 부족으로 인한 최근 실패가 감지되면 true */
  hasAlert: boolean;
  /** 감지된 실패 건수 */
  count: number;
  /** 가장 최근 실패 시각 (ISO) */
  lastFailedAt: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// 한 번에 스캔하는 최근 실패 행 수 상한
const SCAN_LIMIT = 500;

/**
 * 현재 사용자의 최근 발송 실패 중 "잔액 부족" 사유가 있는지 감지한다.
 * RLS(users_select_own_recipients)에 의해 본인 발송 내역의 수신자 행만 조회된다.
 */
export function useBalanceFailureAlert(userId?: string | null): BalanceFailureAlertState {
  const [count, setCount] = useState(0);
  const [lastFailedAt, setLastFailedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchAlert = useCallback(async () => {
    if (!userId) {
      setCount(0);
      setLastFailedAt(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const sinceIso = new Date(
        Date.now() - BALANCE_FAILURE_LOOKBACK_HOURS * 3600_000,
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from("send_recipients")
        .select("id, fail_reason, created_at")
        .eq("status", "fail")
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(SCAN_LIMIT);

      if (queryError) throw new Error(queryError.message);

      const matches = (data ?? []).filter((row: { fail_reason?: string | null }) =>
        isBalanceFailureReason(row.fail_reason),
      );

      if (!mountedRef.current) return;
      setCount(matches.length);
      setLastFailedAt(matches.length > 0 ? matches[0].created_at : null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setCount(0);
      setLastFailedAt(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchAlert();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAlert]);

  return { hasAlert: count > 0, count, lastFailedAt, loading, error, refresh: fetchAlert };
}